import { type FormEvent, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { PASSWORD_MIN_LENGTH } from "../../shared/constants";
import { emailOnlySchema, passwordResetConfirmSchema } from "../../shared/schemas";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useAuthOptions } from "../auth/useAuthOptions";
import { Alert, AuthLayout, focusFirstInvalid, TextField } from "../components/ui";
import { apiFailure, type FieldErrors, validate } from "./forms";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  // Keep the token in memory and out of the visible URL / history.
  const [token] = useState(() => {
    const t = params.get("token");
    if (t) window.history.replaceState(null, "", "/resetare-parola");
    return t;
  });
  return token ? <NewPasswordForm token={token} /> : <RequestResetForm />;
}

function RequestResetForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const options = useAuthOptions();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMessage("");
    const v = validate(emailOnlySchema, { email });
    if (!v.ok) {
      setErrors(v.errors);
      focusFirstInvalid(formRef.current);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await api("POST", "/auth/password-reset/request", v.data);
      setSent(true);
    } catch (err) {
      const f = apiFailure(err);
      setErrors(f.fields);
      setMessage(f.message);
    } finally {
      setBusy(false);
    }
  }

  if (options && !options.emailEnabled) {
    return (
      <AuthLayout>
        <div className="stack" style={{ gap: 8 }}>
          <h1 className="h1">Resetează parola</h1>
          <p className="muted" style={{ margin: 0 }}>
            Resetarea prin e-mail nu este disponibilă momentan. Scrie echipei Meniova: primești un link pentru
            o parolă nouă.
          </p>
        </div>
        <p className="auth-foot">
          <Link to="/login">Înapoi la autentificare</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 8 }}>
        <h1 className="h1">Resetează parola</h1>
        <p className="muted" style={{ margin: 0 }}>
          Îți trimitem un link pentru o parolă nouă.
        </p>
      </div>
      {message && (
        <Alert tone="error" live>
          {message}
        </Alert>
      )}
      {sent ? (
        <Alert tone="info" title="Verifică e-mailul." live>
          Dacă adresa are un cont Meniova, ți-am trimis un link valabil o oră.
        </Alert>
      ) : (
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <TextField
            id="reset-email"
            label="E-mail"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
          />
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? "Se trimite…" : "Trimite linkul"}
          </button>
        </form>
      )}
      <p className="auth-foot">
        <Link to="/login">Înapoi la autentificare</Link>
      </p>
    </AuthLayout>
  );
}

function NewPasswordForm({ token }: { token: string }) {
  const navigate = useNavigate();
  const { state, refresh } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMessage("");
    const v = validate(passwordResetConfirmSchema, { token, password });
    const errs: FieldErrors = v.ok ? {} : v.errors;
    if (!errs.password && confirm !== password) errs.confirm = "Parolele nu coincid.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      if (errs.token) setMessage(errs.token);
      focusFirstInvalid(formRef.current);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await api("POST", "/auth/password-reset/confirm", { token, password });
      // Every session was signed out on the server; sync local state before going to login.
      if (state.status === "authenticated") await refresh();
      navigate("/login?reset=1", { replace: true });
    } catch (err) {
      const f = apiFailure(err);
      setErrors(f.fields);
      setMessage(f.message || f.fields.token || "");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 8 }}>
        <h1 className="h1">Alege o parolă nouă</h1>
        <p className="muted" style={{ margin: 0 }}>
          După schimbare, toate sesiunile deschise se închid.
        </p>
      </div>
      {message && (
        <Alert tone="error" live>
          {message} <Link to="/resetare-parola">Cere un link nou</Link>
        </Alert>
      )}
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <TextField
          id="new-password"
          label={`Parola nouă (minimum ${PASSWORD_MIN_LENGTH} caractere)`}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
        />
        <TextField
          id="new-password-confirm"
          label="Confirmă parola nouă"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={errors.confirm}
        />
        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? "Se salvează…" : "Salvează parola"}
        </button>
      </form>
    </AuthLayout>
  );
}
